import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack } from "@mui/material";
import CustomLayout from "../../../../components/layout";
import PrimaryButton from "../../../../components/buttons/primary-button";
import SecondaryButton from "../../../../components/buttons/secondary-button";
import LoanInfoCard from "../../../../components/info-card";
import PathSelectionCard from "../../../../components/path-selection-card";
import Heading4 from "../../../../components/typography/heading-04";
import Heading5 from "../../../../components/typography/heading-05";
import Body3 from "../../../../components/typography/body-03";
import CustomAlert from "../../../../components/custom-alert";
import AuraInProgressBanner from "../../../../components/aura-in-progress-banner";
import {
	setSelectedPath,
	setCurrentStage,
	selectSelectedPath,
	selectLoanGuid,
	selectIncomeDocumentationType,
} from "../../../../features/current-loan/currentLoanSlice";
import { useSyncAuraProgress } from "../../../../features/current-loan/useSyncAuraProgress";
import { isDSCRstyle } from "../../../../utils/income-documentation-helpers";
import useAuraInProgress from "../../../../hooks/useAuraInProgress";
import useIsEnhancedLoan from "../../../../hooks/useIsEnhancedLoan";
import { VERBIAGE_CONSTANTS } from "../../../../utils/verbiage.constants";
import styles from "./index.module.scss";
import {
	AURA_STEPS,
	NON_AURA_STEPS,
} from "../../../../utils/aura.constants";

const PathSelectPage = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const savedPath = useSelector(selectSelectedPath);
	const loanGuid = useSelector(selectLoanGuid);
	useSyncAuraProgress();
	const incomeDocumentationType = useSelector(selectIncomeDocumentationType);
	const dscrStyle = isDSCRstyle(incomeDocumentationType);

	const [selectedPath, setPath] = useState(savedPath || "");
	// AURA is offered only for Enhanced Conditions loans; standard loans go Without AURA.
	const { isEnhanced, checking: checkingEnhanced } = useIsEnhancedLoan(loanGuid);
	const auraUnavailable = !checkingEnhanced && !isEnhanced;
	// No path changes while an AURA run is in flight.
	const { locked: auraLocked } = useAuraInProgress(loanGuid);

	React.useEffect(() => {
		if (auraUnavailable) setPath("non-aura");
	}, [auraUnavailable]);

	// If no active loan, redirect to upload
	React.useEffect(() => {
		if (!loanGuid) navigate("/current-loan");
	}, [loanGuid, navigate]);

	// Qualify is gated behind the Dashboard's Income Documentation Type prerequisite
	React.useEffect(() => {
		if (loanGuid && !incomeDocumentationType) navigate("/current-loan/dashboard");
	}, [loanGuid, incomeDocumentationType, navigate]);

	const handleBegin = () => {
		if (auraLocked) return;
		dispatch(setSelectedPath(selectedPath));
		dispatch(setCurrentStage("qualify"));
		if (selectedPath === "aura") {
			navigate("/current-loan/qualify/aura");
		} else {
			navigate("/current-loan/qualify/non-aura");
		}
	};

	return (
		<CustomLayout>
			<Box className={styles.pageRoot}>
				<LoanInfoCard loanId={loanGuid} />
				<Stack direction="column" mb={3}>
					<Heading4 text="Step 3: Qualify" fontType="semibold" />
					<Body3
						text="Choose your qualification path to begin"
						extraClass={styles.subTitle}
					/>
				</Stack>

				<Stack direction="column" mb={2}>
					<Heading5 text="Choose qualification path" />
					<Body3
						text="Select how you want to qualify this loan. You can switch paths at any time."
						extraClass={styles.subTitle}
					/>
				</Stack>

				<AuraInProgressBanner show={auraLocked} />

				<CustomAlert
					subject="Incomplete application data detected"
					details={
						dscrStyle
							? "Gross Monthly Rental Income for the subject property is missing. Confirm your Proposed Housing Expense (PITIA) is accurate. This is a DSCR loan — AURA calculates DSCR from the subject property's rental income and proposed housing expense."
							: "Employment & income fields are empty. Assets section has no accounts. You can still proceed, but AURA requires complete income and asset data to run eligibility."
					}
					severity="warning"
					extraClass={styles.warningBanner}
				/>

				{auraUnavailable && (
					<CustomAlert
						details={VERBIAGE_CONSTANTS.AURA_ENHANCED_ONLY_NOTE}
						severity="info"
						extraClass={styles.warningBanner}
					/>
				)}

				<Stack direction="row" gap={3} className={styles.cardRow}>
					<PathSelectionCard
						selected={selectedPath === "aura"}
						onSelect={() => setPath("aura")}
						disabled={auraUnavailable || auraLocked}
						badge="Recommended"
						badgeColor="green"
						title="Submit with AURA"
						description="Re-issue credit and run automated underwriting to get upfront eligibility certainty before pricing."
						steps={AURA_STEPS}
						recommended
					/>
					<PathSelectionCard
						selected={selectedPath === "non-aura"}
						onSelect={() => setPath("non-aura")}
						badge="Less certainty"
						badgeColor="grey"
						title="Submit without AURA"
						description="Skip credit re-issue and AURA. Enter credit profile manually. Manual underwriter review will apply."
						steps={NON_AURA_STEPS}
						disabled={auraLocked}
					/>
				</Stack>

				<Box mt={3}>
					<PrimaryButton
						text="Begin qualification →"
						onClick={handleBegin}
						disabled={!selectedPath || auraLocked || (selectedPath === "aura" && checkingEnhanced)}
					/>
				</Box>

				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					mt={3}
					className={styles.actionBar}
				>
					<Stack direction="row" alignItems="center" gap={1.5}>
						<SecondaryButton
							text="← Change path"
							onClick={() => setPath("")}
							disabled={auraLocked}
						/>
						<SecondaryButton
							text="Save and close"
							onClick={() => navigate("/home")}
						/>
					</Stack>
					<Stack direction="row" alignItems="center" gap={1.5}>
						<Body3
							text="Complete all sub-steps to qualify."
							extraClass={styles.hintText}
						/>
						<PrimaryButton
							text="Save and continue →"
							onClick={handleBegin}
							disabled={!selectedPath || auraLocked || (selectedPath === "aura" && checkingEnhanced)}
						/>
					</Stack>
				</Stack>

				<Box className={styles.disclaimer}>
					Not a commitment to lend. All loans subject to underwriting approval.
				</Box>
			</Box>
		</CustomLayout>
	);
};

export default PathSelectPage;
